import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { parseISO } from 'date-fns';
import { Avatar, ButtonCondensed, Badge } from '@companycam/slab-web';

import { translate } from 'translations/i18n';
import { formatDate } from 'helpers/intl-helper';
import { useCurrentCompany } from 'javascripts/hooks';
import ListItem from 'components/shared/DesignSystem/ListItem';
import { getDisplayNameFromUser } from 'helpers/strings';
import collabColors from '../collaboration/collabColors';
import { USER_ACTIVE_STATUS, USER_DELETED_STATUS } from './constants';

// #region styled-components

const ContentPrimary = styled.span`
  display: flex;
  ${(props) => {
    if (!props.projectUsersModalEnabled) {
      return `
        flex-direction: row;
        justify-content: space-between;
      `;
    }

    return ``;
  }}
`;
const Text = styled.span`
  min-width: 1px;
  ${(props) => {
    if (props.projectUsersModalEnabled) {
      return `width: 225px`;
    }

    return ``;
  }}
`;
const Badges = styled.span`
  display: flex;
  align-items: center;
  gap: var(--cc_size_spacing_s);
  ${(props) => {
    if (props.projectUsersModalEnabled) {
      return `padding-left: 15px`;
    }

    return ``;
  }}
`;
const AssignedBadge = styled(Badge)`
  ${(props) =>
    props.isDeactivated
      ? css`
          opacity: 0.5;
        `
      : null}
`;
const Name = styled.div`
  font-family: 'Averta', system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: var(--cc_size_text_m);
  font-weight: 600;
  color: ${(props) =>
    props.isDeactivated
      ? 'var(--cc_color_text_nonessential)'
      : 'var(--cc_color_text_default)'};
`;
const Details = styled.div`
  font-size: var(--cc_size_text_xs);
  margin-block-start: var(--cc_size_spacing_xs);
  color: ${(props) =>
    props.isDeactivated
      ? 'var(--cc_color_text_nonessential)'
      : 'var(--cc_color_text_subtle)'};
`;
const ContentAfter = styled.span`
  display: flex;
  justify-content: flex-end;
  width: 124px;
  ${(props) => {
    if (props.projectUsersModalEnabled) {
      return `padding-right: 14px`;
    }

    return ``;
  }}
`;
// #endregion

const ProjectUser = ({ user, handleUnassign, showUnassignButton = false }) => {
  const {
    id,
    name,
    lastContributedAt,
    avatar,
    company,
    colorCode,
    assignedAt,
    assignedUser: userObject,
  } = user;
  const { status } = userObject;
  const { id: companyId, name: companyName } = company;
  const { currentCompany } = useCurrentCompany();
  const isDifferentCompany = currentCompany.node_id !== companyId;

  const displayName = name || getDisplayNameFromUser(userObject);
  const isDeactivated = status === USER_DELETED_STATUS;

  const userIsUnassignable =
    assignedAt && showUnassignButton && !isDifferentCompany;
  const handleUnassignUser = () => handleUnassign(id);

  return (
    <ListItem
      key={id}
      contentBefore={
        <Avatar
          data-testid="project-users-modal__avatar"
          iconName={name ? undefined : 'account'}
          fullName={name}
          imageSrc={avatar}
          size="large"
          borderColor={collabColors[colorCode]}
        />
      }
      contentPrimary={
        <ContentPrimary projectUsersModalEnabled>
          <Text projectUsersModalEnabled>
            <Name
              data-testid="project-users-modal__user-name"
              isDeactivated={isDeactivated}
            >
              {`${displayName} ${isDifferentCompany ? `(${companyName})` : ''}`}
            </Name>
            {lastContributedAt && status === USER_ACTIVE_STATUS ? (
              <Details isDeactivated={isDeactivated}>
                {/* TODO: i18n-aware format lastActiveDate when we know in what format the backend will send it */}
                {translate('projects.users.listItem.lastContributedAt', {
                  lastContributedAt: formatDate(
                    new Date(parseISO(lastContributedAt)),
                  ),
                })}
              </Details>
            ) : null}
          </Text>
          <Badges projectUsersModalEnabled>
            {isDeactivated ? (
              <Badge color="destroy" iconName="account-off">
                {translate('projects.users.deactivated')}
              </Badge>
            ) : null}
            {assignedAt ? (
              <AssignedBadge
                data-testid="project-users-modal__badge"
                isDeactivated={isDeactivated}
                color="success"
                iconName="account-check"
              >
                {translate('projects.users.assigned')}
              </AssignedBadge>
            ) : null}
          </Badges>
        </ContentPrimary>
      }
      contentAfter={
        <ContentAfter projectUsersModalEnabled>
          {userIsUnassignable ? (
            <ButtonCondensed
              color="destroy"
              icon={{
                name: 'account-remove',
                position: 'before',
              }}
              onClick={handleUnassignUser}
              data-testid="project-users-modal__button-unassign-user"
            >
              {translate('assignUsers.unassignButtonLabel')}
            </ButtonCondensed>
          ) : null}
        </ContentAfter>
      }
    />
  );
};

ProjectUser.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    lastContributedAt: PropTypes.string,
    avatar: PropTypes.string,
    company: PropTypes.object,
    colorCode: PropTypes.string,
    assignedAt: PropTypes.string,
    assignedUser: PropTypes.object,
  }),
  handleUnassign: PropTypes.func,
  showUnassignButton: PropTypes.bool,
};

export default ProjectUser;
